import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MagazineService, IncrementResponse, BoostResponse, BoostStatusResponse } from 'app/services/magazine/magazine.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-liste-magazine',
  templateUrl: './liste-magazine.component.html',
  styleUrls: ['./liste-magazine.component.scss']
})
export class ListeMagazineComponent implements OnInit {
   
  magazineListe: any;
  filteredMagazines: any;
  dataSource = new MatTableDataSource();
  searchQuery: string = '';
  sortAscending: boolean = true;
  isSearching: boolean = false;
  searchTimeout: any;

  // Latest magazine & boost status
  latestMagazine: any = null;
  latestBoostActive: boolean = false;
  latestBoostDetails: any = null;

  constructor(private magazineService: MagazineService) { }

  ngOnInit(): void {
    this.magazineService.refreshNedeed.subscribe(() => this.getData());
    this.getData();
  }

  getData() {
    this.magazineService.getListMagazine().subscribe({
      next: (data) => {
        this.magazineListe = data;
        this.sortMagazines();
        this.applySearch();
        this.detectLatestMagazine();
      },
      error: (error) => {
        console.error('Erreur lors du chargement des magazines:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger la liste des magazines.'
        });
      }
    });
  }

  // Identify the latest magazine (by createdAt or ID)
  detectLatestMagazine() {
    if (!this.magazineListe || this.magazineListe.length === 0) return;
    
    const sorted = [...this.magazineListe].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : a.id;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : b.id;
      return dateB - dateA;
    });
    this.latestMagazine = sorted[0];
    
    // Check boost status for this magazine
    this.checkBoostStatus(this.latestMagazine.id);
  }

  checkBoostStatus(magazineId: number) {
    this.magazineService.getBoostStatusForMagazine(magazineId).subscribe({
      next: (response: BoostStatusResponse) => {
        this.latestBoostActive = response.hasBoost;
        this.latestBoostDetails = response.boost || null;
      },
      error: (err) => {
        console.error('Failed to get boost status:', err);
        this.latestBoostActive = false;
        this.latestBoostDetails = null;
      }
    });
  }

  onSearchChange() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.isSearching = true;
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
      this.isSearching = false;
    }, 300);
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredMagazines = null;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    if (this.magazineListe) {
      this.filteredMagazines = this.magazineListe.filter((magazine: any) => {
        const searchFields = [
          magazine.name?.toLowerCase() || '',
          magazine.nbr_telechargement?.toString() || ''
        ];
        return searchFields.some(field => field.includes(query));
      });
      this.sortFilteredMagazines();
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.filteredMagazines = null;
    this.isSearching = false;
  }

  toggleSort() {
    this.sortAscending = !this.sortAscending;
    if (this.searchQuery && this.filteredMagazines) {
      this.sortFilteredMagazines();
    } else {
      this.sortMagazines();
    }
  }

  sortMagazines() {
    if (!this.magazineListe) return;
    this.magazineListe.sort((a: any, b: any) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return this.sortAscending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }

  sortFilteredMagazines() {
    if (!this.filteredMagazines) return;
    this.filteredMagazines.sort((a: any, b: any) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return this.sortAscending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }

  highlightSearchText(text: string): string {
    if (!this.searchQuery || !text) return text;
    const regex = new RegExp(`(${this.searchQuery})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  isMagazineHighlighted(magazine: any): boolean {
    if (!this.searchQuery) return false;
    const query = this.searchQuery.toLowerCase();
    return magazine.name?.toLowerCase().includes(query) || false;
  }

  // === BOOST ACTIONS (only for latest magazine) ===
  activateBoostForLatest() {
    if (!this.latestMagazine) return;

    Swal.fire({
      title: 'Activer un boost sponsorisé',
      html: `
        <div style="text-align: left;">
          <p>Magazine: <strong>${this.latestMagazine.name}</strong> (Dernière édition)</p>
          <label for="multiplier">Multiplicateur ( > 1.0 ) :</label>
          <input id="multiplier" class="swal2-input" type="number" step="0.1" min="1.1" value="2.0">
          <label for="hours">Durée (heures, 1-168) :</label>
          <input id="hours" class="swal2-input" type="number" min="1" max="168" value="24">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Activer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#ff9800',
      preConfirm: () => {
        const multiplier = parseFloat((document.getElementById('multiplier') as HTMLInputElement).value);
        const hours = parseInt((document.getElementById('hours') as HTMLInputElement).value, 10);
        if (isNaN(multiplier) || multiplier <= 1.0) {
          Swal.showValidationMessage('Le multiplicateur doit être supérieur à 1.0');
          return false;
        }
        if (isNaN(hours) || hours < 1 || hours > 168) {
          Swal.showValidationMessage('La durée doit être comprise entre 1 et 168 heures');
          return false;
        }
        return { multiplier, hours };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const { multiplier, hours } = result.value;
        Swal.fire({
          title: 'Activation en cours...',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });

        this.magazineService.activateBoost(this.latestMagazine.id, multiplier, hours).subscribe({
          next: (response: BoostResponse) => {
            Swal.fire({
              icon: 'success',
              title: 'Boost activé !',
              html: `
                <p>Le magazine <strong>${this.latestMagazine.name}</strong> bénéficie d'un boost.</p>
                <p>Multiplicateur: ${response.multiplier}x<br>
                Durée: ${response.durationHours} heures<br>
                Expire le: ${new Date(response.expiresAt).toLocaleString()}</p>
              `,
              confirmButtonColor: '#ff9800'
            });
            this.checkBoostStatus(this.latestMagazine.id);
            this.getData();
          },
          error: (err) => {
            console.error('❌ Boost activation error:', err);
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: err.message || 'Impossible d\'activer le boost.'
            });
          }
        });
      }
    });
  }

  deactivateBoostForLatest() {
    if (!this.latestMagazine) return;

    Swal.fire({
      title: 'Désactiver le boost ?',
      text: `Voulez-vous vraiment désactiver le boost du magazine "${this.latestMagazine.name}" ?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, désactiver',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Désactivation en cours...',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });

        this.magazineService.deactivateBoost(this.latestMagazine.id).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: 'Boost désactivé',
              text: `Le boost a été retiré du magazine ${this.latestMagazine.name}.`,
              timer: 2000,
              showConfirmButton: false
            });
            this.checkBoostStatus(this.latestMagazine.id);
            this.getData();
          },
          error: (err) => {
            console.error('❌ Deactivate error:', err);
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: err.message || 'Impossible de désactiver le boost.'
            });
          }
        });
      }
    });
  }

  // === OTHER ACTIONS ===
  incrementDownloads(magazine: any, event: Event): void {
    event.stopPropagation(); 
    
    Swal.fire({
      title: 'Incrémenter les téléchargements ?',
      text: `Cela ajoutera un nombre aléatoire (10-500) au compteur downloadCount du magazine "${magazine.name}".`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2196f3',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, incrémenter',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Incrémentation en cours...',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });

        this.magazineService.incrementDownloadCount(magazine.id).subscribe({
          next: (response: IncrementResponse) => {
            Swal.fire({
              icon: 'success',
              title: 'Compteur incrémenté !',
              html: `
                <p>Le compteur a été augmenté de <strong>${response.magazine.increment}</strong>.</p>
                <p>Ancienne valeur : ${response.magazine.oldCount}<br>
                Nouvelle valeur : ${response.magazine.newCount}</p>
              `,
              confirmButtonColor: '#2196f3'
            });
            this.getData();
          },
          error: (err) => {
            console.error('❌ Increment error:', err);
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: err.message || 'Impossible d\'incrémenter le compteur.'
            });
          }
        });
      }
    });
  }

  SupprimerMagazine(magazine: any) {
    Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: `Voulez-vous vraiment supprimer le magazine "${magazine.name}" ?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          html: 'Suppression du magazine et de l\'image associée...',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });

        this.magazineService.deleteMagazine(magazine.id).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: 'Supprimé !',
              text: 'Le magazine a été supprimé avec succès',
              timer: 2000,
              showConfirmButton: false
            });
            this.getData();
          },
          error: (error: any) => {
            console.error('❌ Erreur:', error);
            let errorMessage = 'Impossible de supprimer le magazine.';
            if (error.status === 404) errorMessage += ' Le magazine n\'existe pas.';
            else if (error.status === 403) errorMessage += ' Permissions insuffisantes.';
            else if (error.status === 401) errorMessage += ' Session expirée.';
            Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage });
          }
        });
      }
    });
  }

  onImageError(event: any) {
    event.target.src = 'https://via.placeholder.com/80x100/2196F3/FFFFFF?text=Magazine';
    event.target.onerror = null;
  }
}